import React from "react";
import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../breakpoints";
import { TOOLBAR_SHADOW } from "./inputs.styles";

const StyledSearch = styled.div(({ $disabled = false }) => ({
    width: "166px",
    height: "35px",
    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-3"]}`,
    background: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
    borderRadius: tokens.globals["border-radius"],
    boxShadow: TOOLBAR_SHADOW,
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
    opacity: $disabled ? 0.6 : 1,

    "&:focus-within": $disabled ? {} : {
        borderColor: tokens.colors.brand.blue["500"],
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "-1px",
    },

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
    },
}));

const StyledSearchIcon = styled.i({
    width: "16px",
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["base-16px"],
    lineHeight: "16px",
    textAlign: "center",
    flexShrink: 0,
});

/*
 * Occupies the same slot as StyledSearchIcon (same size/color, no padding or border) so swapping
 * between the two never changes StyledSearch's box dimensions.
 */
const StyledSearchClearButton = styled.button({
    width: "16px",
    height: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    background: "transparent",
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["base-16px"],
    flexShrink: 0,
    cursor: "pointer",

    "&:hover": { color: tokens.colors.brand.blue["500"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },
});

const StyledSearchInput = styled.input({
    flex: "1 1 0",
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",

    "&::placeholder": {
        color: tokens.colors.text.secondary,
    },

    "&:disabled": {
        cursor: "not-allowed",
    },
});

/**
 * DataTable toolbar search box; the search icon swaps to a clear button once there's a value.
 * @param {Function} [props.onChange] Called with `(value, event)`; `""` when cleared.
 * Also: value, placeholder, ariaLabel, disabled, disabledReason, className (self-explanatory).
 */
function DataTableSearchInput({
    value = "",
    onChange,
    placeholder = "Search",
    ariaLabel = "Search",
    disabled = false,
    disabledReason,
    className,
}) {
    return (
        <StyledSearch className={className} $disabled={disabled} title={disabled ? disabledReason : undefined}>
            {value ? (
                <StyledSearchClearButton
                    type="button"
                    disabled={disabled}
                    aria-label="Clear search"
                    onClick={(event) => onChange && onChange("", event)}
                >
                    <i className="fal fa-times" aria-hidden="true" />
                </StyledSearchClearButton>
            ) : (
                <StyledSearchIcon className="fal fa-search" aria-hidden="true" />
            )}
            <StyledSearchInput
                placeholder={placeholder}
                aria-label={ariaLabel}
                value={value}
                disabled={disabled}
                onChange={(event) => onChange && onChange(event.target.value, event)}
            />
        </StyledSearch>
    );
}

DataTableSearchInput.displayName = "DataTableSearchInput";

export default DataTableSearchInput;
